import { FunctionalComponent } from "preact";
import { useState, useEffect } from "preact/hooks";
import { Bookmark, Tag } from "../utils/api";
import { TagInput } from "./TagInput";

interface BookmarkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string | null, data: Partial<Bookmark>) => Promise<void>;
  bookmark?: Bookmark | null;
  availableTags: Tag[];
  onCreateTag?: (name: string) => void;
}

export const BookmarkModal: FunctionalComponent<BookmarkModalProps> = ({
  isOpen,
  onClose,
  onSave,
  bookmark,
  availableTags,
  onCreateTag,
}) => {
  const [formData, setFormData] = useState({
    url: "",
    title: "",
    description: "",
    notes: "",
    highlight: "",
    tags: [] as string[],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (bookmark) {
      let parsedTags: string[] = [];
      if (bookmark.tags) {
        try {
          const tags = JSON.parse(bookmark.tags);
          parsedTags = Array.isArray(tags) ? tags : [];
        } catch {
          parsedTags = [];
        }
      }
      setFormData({
        url: bookmark.url,
        title: bookmark.title,
        description: bookmark.description || "",
        notes: bookmark.notes || "",
        highlight: bookmark.highlight || "",
        tags: parsedTags,
      });
    } else {
      setFormData({
        url: "",
        title: "",
        description: "",
        notes: "",
        highlight: "",
        tags: [],
      });
    }
  }, [bookmark, isOpen]);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setError("");

    if (!formData.url || !formData.title) {
      setError("URL and title are required");
      return;
    }

    try {
      new URL(formData.url);
    } catch {
      setError("Invalid URL");
      return;
    }

    setSaving(true);
    try {
      await onSave(bookmark?.id || null, {
        ...formData,
        tags: formData.tags.length > 0 ? JSON.stringify(formData.tags) : undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-screen items-center justify-center p-4">
        <div
          class="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />
        <div class="relative bg-white dark:bg-stone-900 rounded-2xl shadow-2xl max-w-lg w-full border border-stone-200 dark:border-stone-800 overflow-hidden">
          <div class="flex items-center justify-between p-6 border-b border-stone-100 dark:border-stone-800/50 bg-stone-50/50 dark:bg-stone-800/30">
            <h3 class="text-xl font-display font-bold text-stone-900 dark:text-stone-50">
              {bookmark ? "Edit Bookmark" : "Add Bookmark"}
            </h3>
            <button
              onClick={onClose}
              class="p-2 rounded-full text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} class="p-6 space-y-5">
            {error && (
              <div class="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm border border-red-100 dark:border-red-900/30">
                {error}
              </div>
            )}

            <div class="space-y-1.5">
              <label class="block text-sm font-semibold text-stone-700 dark:text-stone-300">
                URL
              </label>
              <input
                type="url"
                value={formData.url}
                onInput={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    url: (e.target as HTMLInputElement).value,
                  }))
                }
                class="w-full px-4 py-2.5 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                placeholder="https://example.com"
                required
              />
            </div>

            <div class="space-y-1.5">
              <label class="block text-sm font-semibold text-stone-700 dark:text-stone-300">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onInput={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    title: (e.target as HTMLInputElement).value,
                  }))
                }
                class="w-full px-4 py-2.5 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-serif text-lg"
                placeholder="Page title"
                required
              />
            </div>

            <div class="space-y-1.5">
              <label class="block text-sm font-semibold text-stone-700 dark:text-stone-300">
                Description
              </label>
              <textarea
                value={formData.description}
                onInput={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: (e.target as HTMLTextAreaElement).value,
                  }))
                }
                rows={3}
                class="w-full px-4 py-2.5 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-serif"
                placeholder="Brief description..."
              />
            </div>

            <div class="space-y-1.5">
              <label class="block text-sm font-semibold text-stone-700 dark:text-stone-300">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onInput={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    notes: (e.target as HTMLTextAreaElement).value,
                  }))
                }
                rows={3}
                class="w-full px-4 py-2.5 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-serif"
                placeholder="Personal notes..."
              />
            </div>

            <TagInput
              selectedTags={formData.tags}
              availableTags={availableTags}
              onChange={(tags) => setFormData((prev) => ({ ...prev, tags }))}
              onCreateTag={onCreateTag}
            />

            <div class="flex justify-end gap-3 pt-6 border-t border-stone-100 dark:border-stone-800/50">
              <button
                type="button"
                onClick={onClose}
                class="px-5 py-2.5 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-full transition-all font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                class="px-6 py-2.5 bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-sm hover:shadow-md transition-all"
              >
                {saving && (
                  <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {bookmark ? "Save Changes" : "Add Bookmark"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
